import React, { createContext, useContext, useState, useEffect } from "react";
import { GetUserProfile, GetCountries } from "../../services/backendServices";
import { STORAGE_KEYS } from "../../Util/key_helper";
import { decryptData, encryptData } from "../../Util/secureStorage";
// Create a context for ProfileData
const ProfileContext = createContext();

/**
 * ProfileContextProvider: A provider component that manages and supplies profile data to its children components.
 * It fetches and stores user profile data, tracks loading and fetch status, and provides functions to reset or refetch the data.
 */
export const ProfileContextProvider = ({ children }) => {

    const [profileData, setProfileData] = useState(() => {
        const storedData = localStorage.getItem(STORAGE_KEYS.PROFILE);
        return storedData ? decryptData(storedData) : null;
    });
    const [profileLoading, setProfileLoading] = useState(true);  // State to track loading status for profile data
    const [refetchProfileData, setRefetchProfileData] = useState(true);  // State to track if profile data needs to be refetched
    const [countries, setCountries] = useState(new Map());
    // fetchUserProfile: A function that fetches the user profile data from the backend.
    const fetchUserProfile = async () => {
        try {
            const res = await GetUserProfile();  // Make API call to fetch user profile
            if (res.status) {
                const minimalData = {
                    is_whitelabel: res?.data?.is_whitelabel,
                    domain_name: res?.data?.domain_name,
                    is_subuser_account_add_allow: res?.data?.is_subuser_account_add_allow,
                    is_subuser_json_bridge_allow: res?.data?.is_subuser_json_bridge_allow,
                    is_subuser_custom_bridge_allow: res?.data?.is_subuser_custom_bridge_allow,
                    profile_status: res?.data?.profile_status,
                    actual_email: res?.data?.actual_email,
                    telegram_feature_enabled: res?.data?.telegram_feature_enabled,
                    user_id: res?.user_id?.user_id
                }
                localStorage.setItem(STORAGE_KEYS.PROFILE,
                    encryptData(minimalData)
                );
                setProfileData(res.data);  // Update the state with fetched profile data
                setRefetchProfileData(false);  // Set refetch status to false once data is successfully fetched
            }
        } catch (error) {
            console.error("Error fetching user profile", error);  // Log error if fetching fails
        } finally {
            setProfileLoading(false);  // Set loading state to false after fetching process (either success or failure)
        }
    };
    useEffect(() => {
        const storedData = localStorage.getItem(STORAGE_KEYS.PROFILE);
        if (storedData) {
            const decryptedData = decryptData(storedData);
            setProfileData((prev) => ({ ...prev, ...decryptedData }));
        }
    }, []);
    // Effect hook to fetch user profile data on component mount, if needed
    useEffect(() => {
        if (refetchProfileData) {
            fetchUserProfile();  // Trigger fetching of profile data if conditions are met

        }
    }, [refetchProfileData]);  // Dependency array: re-run the effect when refetchProfileData changes
    useEffect(() => {
        fetchCountryData();
    }, [])
    // resetProfileContext: A function to reset the profile context, which triggers a refetch of profile data.    
    const resetProfileContext = () => {
        setProfileData(null);
        setRefetchProfileData(true);  // Reset fetch status to true, prompting the next fetch
    };
    // Function to fetch Country Data
    const fetchCountryData = async () => {
        try {
            const res = await GetCountries();

            if (res.status) {
                const countryMap = new Map();
                res.data.forEach((country) => {
                    countryMap.set(country.name, {
                        name: country.name,
                        mobile_no_length: country.mobile_no_length,
                        code: country.code,
                    });
                });
                setCountries(countryMap);

            } else {
                setCountries(new Map())
            }
        } catch (error) {
            console.error("Failed to fetch country:", error.message);
        }
    };
    return (
        // The ProfileContext.Provider component provides state and functions to the children components
        <ProfileContext.Provider
            value={{
                resetProfileContext, // Provides a function to reset the profile context and refetch the data
                profileData, // Provides the current user profile data
                setProfileData, // Provides the function to set or update the profile data
                profileLoading, // Provides the loading state of profile data
                fetchUserProfile, // Provides the function to fetch the user profile data
                setRefetchProfileData, // Provides the function to manually update the refetch status
                refetchProfileData, // Provides the current refetch status of profile data
                countries, // Countries OPtion 
            }}
        >
            {children}
        </ProfileContext.Provider>
    );
};

// Custom hook to use the ProfileContext
export const useProfileContext = () => useContext(ProfileContext);
